class PromotionService {
  /**
   * @constructor
   */
  constructor() {}

  /**
   * @template T
   * @param {T[]|T|null|undefined} value
   * @returns {T[]}
   */
  toArray(value) {
    return Array.isArray(value) ? value : value == null ? [] : [value];
  }

  /**
   * @param {unknown} value
   * @returns {value is string}
   */
  isNonEmptyString(value) {
    return typeof value === 'string' && value.trim().length > 0;
  }

  /**
   * @param {unknown} value
   * @param {number} [fallback=3]
   * @returns {number}
   */
  parsePriority(value, fallback = 3) {
    if (Array.isArray(value) && value.length > 0)
      return this.parsePriority(value[0]);
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  /**
   * @param {unknown} iso
   * @returns {Date|null}
   */
  parseIsoDateSafe(iso) {
    if (!this.isNonEmptyString(iso)) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  /**
   * @param {Date} now
   * @param {unknown} startIso
   * @param {unknown} endIso
   * @returns {boolean}
   */
  isWithinDateRange(now, startIso, endIso) {
    const start = this.parseIsoDateSafe(startIso);
    const end = this.parseIsoDateSafe(endIso);
    if (start && now < start) return false;
    if (end && now > end) return false;

    return true;
  }

  /**
   * @param {string[]} handlesToMatch
   * @param {Array<{handle?: string}>|unknown} collectionObjects
   * @returns {boolean}
   */
  collectionsIntersectByHandle(handlesToMatch, collectionObjects) {
    if (!Array.isArray(handlesToMatch) || !Array.isArray(collectionObjects))
      return false;
    const set = new Set(handlesToMatch.filter(Boolean).map(String));
    for (const item of collectionObjects) {
      const h = item && item.handle ? String(item.handle) : null;
      if (h && set.has(h)) return true;
    }
    return false;
  }

  /**
   * @param {SelectedMessage[]} messages
   * @returns {SelectedMessage[]}
   */
  deduplicateMessages(messages) {
    const out = [];
    const seen = new Set();
    for (const m of messages) {
      const key = `${m.type}::${m.priority}::${m.text}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(m);
      }
    }
    return out;
  }

  /**
   * @param {SelectedMessage[]} messages
   * @param {string[]} exclusions
   * @param {string} [wrapperClass='promo-messages']
   * @returns {HTMLDivElement}
   */
  buildContainer(messages, exclusions, wrapperClass = 'promo-messages') {
    const container = document.createElement('div');
    container.className = wrapperClass;
    for (const m of messages) {
      const span = document.createElement('span');
      span.textContent = m.text;
      span.setAttribute('data-type', m.type);
      span.setAttribute('data-priority', String(m.priority));
      container.appendChild(span);
    }
    for (const ex of exclusions) {
      const span = document.createElement('span');
      span.textContent = ex;
      span.setAttribute('data-type', 'exclusion');
      container.appendChild(span);
    }
    return container;
  }

  /**
   * @template {'segment'|'product'|'sitewide'} K
   * @param {{ type: K, priority: number, text: string, source: any }} input
   * @returns {SelectedMessage}
   */
  buildMessage(input) {
    return /** @type {SelectedMessage} */ (input);
  }

  /**
   * @param {PromotionSegment[]} segments
   * @param {Date} now
   * @param {string[]} collectionHandles
   * @param {number} limit
   * @returns {SelectedMessage[]}
   */
  selectSegmentMessages(segments, now, collectionHandles, limit) {
    const list = segments
      .filter((s) => this.isWithinDateRange(now, s.startDate, s.endDate))
      .filter((s) => {
        const exceptionCollections = Array.isArray(s.exceptionCollections)
          ? s.exceptionCollections
          : [];
        return !this.collectionsIntersectByHandle(
          collectionHandles,
          exceptionCollections,
        );
      })
      .map((s) =>
        this.buildMessage({
          type: /** @type {'segment'} */ ('segment'),
          priority: this.parsePriority(s.priority),
          text: s.message,
          source: s,
        }),
      )
      .filter((m) => this.isNonEmptyString(m.text))
      .sort((a, b) => a.priority - b.priority);

    return list.slice(0, limit);
  }

  /**
   * @param {ProductPromotion[]} productPromos
   * @param {Date} now
   * @param {string[]} collectionHandles
   * @param {number} limit
   * @returns {SelectedMessage[]}
   */
  selectProductMessages(productPromos, now, collectionHandles, limit) {
    const list = productPromos
      .filter((p) => this.isWithinDateRange(now, p.startDate, p.endDate))
      .filter((p) => {
        const cols = Array.isArray(p.collections) ? p.collections : [];
        const exceptionCollections = Array.isArray(p.exceptionCollections)
          ? p.exceptionCollections
          : [];
        return (
          this.collectionsIntersectByHandle(collectionHandles, cols) &&
          !this.collectionsIntersectByHandle(
            collectionHandles,
            exceptionCollections,
          )
        );
      })
      .map((p) =>
        this.buildMessage({
          type: /** @type {'product'} */ ('product'),
          priority: this.parsePriority(p.priority),
          text: p.message,
          source: p,
        }),
      )
      .filter((m) => this.isNonEmptyString(m.text))
      .sort((a, b) => a.priority - b.priority);

    return list.slice(0, limit);
  }

  /**
   * @param {SiteWidePromotion[]} siteWidePromos
   * @param {Date} now
   * @param {boolean} isLoggedIn
   * @param {number} limit
   * @returns {SelectedMessage[]}
   */
  selectSitewideMessages(siteWidePromos, now, isLoggedIn, limit) {
    const list = siteWidePromos
      .filter((g) => this.isWithinDateRange(now, g.startDate, g.endDate))
      .filter((g) => {
        const requireLogin = Boolean(g.loginOnly);

        return !requireLogin || isLoggedIn;
      })
      .map((g) => {
        const text =
          isLoggedIn && this.isNonEmptyString(g.loggedMessage)
            ? g.loggedMessage
            : g.message;
        return this.buildMessage({
          type: /** @type {'sitewide'} */ ('sitewide'),
          priority: this.parsePriority(g.priority),
          text,
          source: g,
        });
      })
      .filter((m) => this.isNonEmptyString(m.text))
      .sort((a, b) => a.priority - b.priority);

    return list.slice(0, limit);
  }

  /**
   * @param {PromotionSegment[]} segments
   * @param {ProductPromotion[]} productPromos
   * @param {SiteWidePromotion[]} siteWidePromos
   * @param {string[]} collectionHandles
   * @returns {string[]}
   */
  selectExclusions(segments, productPromos, siteWidePromos, collectionHandles) {
    const out = new Set();
    const sourcePromos = [...this.toArray(segments), ...this.toArray(siteWidePromos)];

    for (const promo of sourcePromos) {
      if (!this.isWithinDateRange(new Date(), promo.startDate, promo.endDate)) {
        continue;
      }

      const exceptionCollections = Array.isArray(promo.exceptionCollections)
        ? promo.exceptionCollections
        : [];
      if (
        this.collectionsIntersectByHandle(
          collectionHandles,
          exceptionCollections,
        ) &&
        this.isNonEmptyString(promo.exceptionMessage)
      ) {
        out.add(String(promo.exceptionMessage).trim());
      }
    }

    for (const productPromo of this.toArray(productPromos)) {
      if (
        !this.isWithinDateRange(
          new Date(),
          productPromo.startDate,
          productPromo.endDate,
        )
      ) {
        continue;
      }

      const productCollections = Array.isArray(productPromo.collections)
        ? productPromo.collections
        : [];
      const exceptionCollections = Array.isArray(productPromo.exceptionCollections)
        ? productPromo.exceptionCollections
        : [];
      if (
        this.collectionsIntersectByHandle(collectionHandles, productCollections) &&
        this.collectionsIntersectByHandle(
          collectionHandles,
          exceptionCollections,
        ) &&
        this.isNonEmptyString(productPromo.exceptionMessage)
      ) {
        out.add(String(productPromo.exceptionMessage).trim());
      }
    }
    return Array.from(out);
  }

  /**
   * @param {SiteWidePromotion[]} siteWidePromos
   * @param {string[]} collectionHandles
   * @returns {Set<SiteWidePromotion>}
   */
  getExcludedSitewidePromos(siteWidePromos, collectionHandles) {
    const excluded = new Set();

    for (const g of this.toArray(siteWidePromos)) {
      const exceptionCollections = Array.isArray(g.exceptionCollections)
        ? g.exceptionCollections
        : [];

      if (
        this.collectionsIntersectByHandle(
          collectionHandles,
          exceptionCollections,
        ) &&
        this.isNonEmptyString(g.exceptionMessage)
      ) {
        excluded.add(g);
      }
    }

    return excluded;
  }

  /**
   * @param {string[]} [handles]
   * @returns {GetMessagesResult}
   */
  getMessages(handles) {
    /** @type {string[]} */
    let collectionHandles = Array.isArray(handles) ? handles : [];
    /** @type {boolean} */
    let isLoggedIn = Boolean(window.shopifyVariables.customerId);
    /** @type {Date} */
    let now = new Date();
    /** @type {number} */
    let maxMessages = 3;

    const { segments, product, siteWide } = window.Promotions.data;

    const excludedSitewide = this.getExcludedSitewidePromos(
      siteWide,
      collectionHandles,
    );

    const segMsgs = this.selectSegmentMessages(
      segments,
      now,
      collectionHandles,
      maxMessages,
    );
    const prodMsgs = this.selectProductMessages(
      product,
      now,
      collectionHandles,
      maxMessages,
    );

    const siteMsgs = this.selectSitewideMessages(
      siteWide.filter((g) => !excludedSitewide.has(g)),
      now,
      isLoggedIn,
      maxMessages,
    );

    const messages = this.deduplicateMessages([
      ...segMsgs,
      ...prodMsgs,
      ...siteMsgs,
    ]).slice(0, maxMessages);

    const exclusions = this.selectExclusions(
      segments,
      product,
      siteWide,
      collectionHandles,
    );
    const element = this.buildContainer(messages, exclusions, 'promo-messages');
    const html = element.outerHTML;

    return { messages, exclusions, element, html };
  }
}

class PromoMessagesElement extends HTMLElement {
  static get observedAttributes() {
    return ['data-collections'];
  }

  constructor() {
    super();
    this.service = new PromotionService();
    this._onPromotionsReady = this._onPromotionsReady.bind(this);
  }

  connectedCallback() {
    document.addEventListener('promotions:ready', this._onPromotionsReady);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('promotions:ready', this._onPromotionsReady);
  }

  attributeChangedCallback() {
    this.render();
  }

  _onPromotionsReady() {
    this.render();
  }

  _readCollections() {
    const raw = this.getAttribute('data-collections') || '';
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  render() {
    const collections = this._readCollections();
    const { element, messages, exclusions, html } =
      this.service.getMessages(collections);

    // replace light DOM for easy theming
    this.replaceChildren(element);

    this.dispatchEvent(
      new CustomEvent('promo-messages:rendered', {
        detail: { messages, exclusions, html, collections },
        bubbles: true,
      })
    );
  }
}

if (!customElements.get('promo-messages')) {
  customElements.define('promo-messages', PromoMessagesElement);
}

window.Promotions.PromotionServiceClass = PromotionService;
window.Promotions.promotionService =
  window.Promotions.promotionService || new PromotionService();
/**
 * @param {string[]} [opts]
 * @returns {GetMessagesResult}
 */
window.Promotions.getPromoMessages = function getPromoMessages(opts) {
  return window.Promotions.promotionService.getMessages(opts);
};
